import { useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from './DataTable'
import type { DataTableColumn } from './DataTable'
import { StatCard } from './StatCard'

/**
 * Budget burn: who is spending faster than their budget allows, and who runs
 * out when.
 *
 * WHY PACE IS THE SORT KEY AND NOT DAYS REMAINING. Onboarding sets each
 * customer $100 per 30 days, so most budgets REFILL. Two people can both be
 * 80 per cent spent and mean opposite things: one is six days into the period
 * and will be refused well before it resets, the other is twenty-seven days
 * in and is simply finishing a normal month. Ranking on days-left cannot tell
 * them apart, so this ranks on share-of-budget over share-of-period.
 *
 * The rate comes from the warehouse and lags a day; the spend and cap come
 * from the gateway and are live. See backend/src/burn.ts for why.
 */

interface BurnUser {
  email: string
  spend: number
  cap: number
  unlimited: boolean
  percent: number | null
  windowSpend: number
  burnPerDay: number
  burnPerDay7: number
  accelerating: boolean
  daysLeft: number | null
  exhaustionDate: string | null
  pace: number | null
  resetAt: string | null
  atCap: boolean
  /** A Claude Code session on a developer key (its id is the session's
   *  metadata JSON), attributed to the key's owner when known. */
  session?: boolean
  attributedTo?: string | null
}

/** Who a row is, never Claude Code's raw session JSON (Maroun, 25 Sep 2026). */
function who(u: BurnUser): string {
  if (!u.session) return u.email
  return u.attributedTo ? `${u.attributedTo} · Claude Code session` : 'Claude Code session (owner unknown)'
}

interface BurnPlatform {
  windowSpend: number
  burnPerDay: number
  projectedMonthEnd: number
}

interface BurnResponse {
  windowDays: number
  users: BurnUser[]
  platform: BurnPlatform
}

function money(value: number): string {
  return `$${value.toFixed(2)}`
}

/** Deliberately "—" and not 0 or ∞: a blank says "not applicable here",
 *  where a number invites someone to act on it. */
function dash(node: string | null): string {
  return node ?? '—'
}

export function BurnPanel({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [data, setData] = useState<BurnResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    apiJson<BurnResponse>('/api/budgets/burn?days=30')
      .then((d) => {
        if (live) {
          setData(d)
          setError(null)
        }
      })
      .catch((e) => {
        if (live) setError(e instanceof ApiError ? e.message : String(e))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [])

  const atCap = useMemo(() => (data?.users ?? []).filter((u) => u.atCap), [data])
  // Ahead of pace by a margin, not merely above 1: a user at 1.05 is noise.
  const overPace = useMemo(
    () => (data?.users ?? []).filter((u) => !u.atCap && (u.pace ?? 0) >= 1.25),
    [data],
  )
  const accelerating = useMemo(
    () => (data?.users ?? []).filter((u) => u.accelerating && !u.atCap),
    [data],
  )

  /** Everyone with something to say about them. A user who is inside their
   *  budget, on pace and not accelerating is not a row worth reading. */
  const rows = useMemo(
    () =>
      (data?.users ?? []).filter(
        (u) => u.atCap || (u.pace ?? 0) >= 1.25 || u.accelerating || (u.daysLeft ?? 999) <= 14,
      ),
    [data],
  )

  const columns: Array<DataTableColumn<BurnUser>> = [
    { key: 'user', header: 'User', render: (u) => who(u) },
    {
      key: 'spend',
      header: 'Spent',
      align: 'right',
      render: (u) => (u.unlimited ? money(u.spend) : `${money(u.spend)} / ${money(u.cap)}`),
    },
    {
      key: 'pct',
      header: '%',
      align: 'right',
      render: (u) => (u.percent === null ? '—' : `${Math.round(u.percent * 100)}%`),
    },
    {
      key: 'pace',
      header: 'Pace',
      align: 'right',
      render: (u) =>
        u.pace === null ? (
          '—'
        ) : (
          <span style={{ color: u.pace >= 1.25 ? tokens.danger : tokens.textPrimary }}>
            {u.pace.toFixed(2)}×
          </span>
        ),
    },
    {
      key: 'burn',
      header: '$/day',
      align: 'right',
      render: (u) => (
        <span>
          {money(u.burnPerDay)}
          {u.accelerating ? (
            <span
              title={`Last 7 days ${money(u.burnPerDay7)}/day against ${money(u.burnPerDay)} over the window`}
              style={{ marginLeft: 6, color: tokens.danger, fontWeight: 600 }}
            >
              ▲
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'left',
      header: 'Days left',
      align: 'right',
      render: (u) => (u.daysLeft === null ? '—' : Math.floor(u.daysLeft).toString()),
    },
    { key: 'out', header: 'Runs out', render: (u) => dash(u.exhaustionDate) },
    {
      key: 'reset',
      header: 'Resets',
      render: (u) => dash(u.resetAt ? u.resetAt.slice(0, 10) : null),
    },
  ]

  if (loading) return <p style={styles.hint}>Loading budget burn…</p>
  if (error) return <p style={styles.error}>{error}</p>
  if (!data) return null
  // A partial or unexpected payload must degrade to a message, never to a
  // thrown render. This panel sits on the Budgets page: reading `platform`
  // off an object that does not have it took the whole page down with it,
  // so setting someone's cap became impossible because a statistic was
  // missing. The panel is the least important thing on this screen and it
  // must fail like it.
  if (!data.platform || !Array.isArray(data.users)) {
    return (
      <p style={styles.hint}>
        Budget burn is unavailable — the rate data came back in a shape this panel does not
        recognise. Spend and caps below are unaffected.
      </p>
    )
  }

  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>Budget burn</h2>
      </div>
      <p style={styles.sectionSubtitle}>
        Pace is share of budget spent divided by share of the period elapsed, so 1.00× is exactly on
        track and 2.00× means the budget will be gone at half-time. Rates are per calendar day over
        the last {data.windowDays} days, not per day worked. Spend and caps are live from the
        gateway; rates come from the nightly copy and lag by up to a day.
      </p>

      <div style={styles.statsRow}>
        <StatCard
          label="At or over cap"
          value={atCap.length}
          {...(atCap.length > 0 ? { hint: 'Being refused now' } : {})}
          tokens={tokens}
          tone={atCap.length > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Ahead of pace"
          value={overPace.length}
          hint="1.25× or faster"
          tokens={tokens}
          tone={overPace.length > 0 ? 'danger' : 'default'}
        />
        <StatCard label="Accelerating" value={accelerating.length} hint="7d rate above window rate" tokens={tokens} />
        <StatCard label="Platform" value={`${money(data.platform.burnPerDay)}/day`} tokens={tokens} />
        <StatCard
          label="Projected month"
          value={money(data.platform.projectedMonthEnd)}
          hint="At the current rate"
          tokens={tokens}
        />
      </div>

      {atCap.length > 0 ? (
        <p style={styles.error}>
          {atCap.length} {atCap.length === 1 ? 'user is' : 'users are'} at or over cap and being
          refused by the gateway: {atCap.map(who).join(', ')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(u) => u.email}
        getRowLabel={(u) => who(u)}
        tokens={tokens}
        emptyLabel="Nobody is at cap, ahead of pace, accelerating, or inside two weeks of their budget."
      />
    </section>
  )
}
